import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { Plus, PencilSimple, Trash, CurrencyEur, Package, Truck, MagnifyingGlass } from "@phosphor-icons/react";

const EMPTY = { product_id: "", supplier_id: "", price: 0, currency: "EUR", notes: "" };

export default function PurchasePrices() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [prices, prods, sups] = await Promise.all([
        api.get("/purchase-prices"),
        api.get("/products"),
        api.get("/suppliers"),
      ]);
      setRows(prices.data || []);
      setProducts(prods.data || []);
      setSuppliers(sups.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ product_id: r.product_id, supplier_id: r.supplier_id, price: r.price, currency: r.currency || "EUR", notes: r.notes || "" });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.supplier_id) {
      return toast.error("Seleziona prodotto e fornitore");
    }
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) {
        await api.put(`/purchase-prices/${editing.id}`, payload);
        toast.success("Prezzo aggiornato");
      } else {
        await api.post("/purchase-prices", payload);
        toast.success("Prezzo creato");
      }
      setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Rimuovere il prezzo ${r.product_sku} · ${r.supplier_name}?`)) return;
    try {
      await api.delete(`/purchase-prices/${r.id}`);
      toast.success("Prezzo rimosso");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const productOpts = useMemo(() => products.map((p) => ({
    value: p.id,
    label: `${p.sku} — ${p.name}`,
    sublabel: p.is_serialized === false ? "Bulk" : "Seriale",
    keywords: [p.sku, p.name, p.manufacturer_code].filter(Boolean),
  })), [products]);

  const supplierOpts = useMemo(() => suppliers.map((s) => ({
    value: s.id, label: s.name, sublabel: s.email || "",
    keywords: [s.name, s.email, s.phone].filter(Boolean),
  })), [suppliers]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.product_sku || "").toLowerCase().includes(needle) ||
      (r.product_name || "").toLowerCase().includes(needle) ||
      (r.supplier_name || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  return (
    <div>
      <PageHeader
        eyebrow="Approvvigionamento"
        title="Listino Acquisto"
        subtitle="Prezzo di acquisto per ogni coppia prodotto · fornitore. Usato per auto-compilare l'ingresso bulk."
        action={
          <button data-testid="new-pp-btn" onClick={openNew} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors flex items-center gap-2">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Nuovo prezzo</span><span className="sm:hidden">Nuovo</span>
          </button>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 max-w-md">
          <MagnifyingGlass size={16} className="text-neutral-400" />
          <input
            data-testid="pp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per SKU, prodotto o fornitore..."
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>

        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">SKU</th>
                <th className="px-4 py-3 label-eyebrow">Prodotto</th>
                <th className="px-4 py-3 label-eyebrow">Fornitore</th>
                <th className="px-4 py-3 label-eyebrow text-right">Prezzo</th>
                <th className="px-4 py-3 label-eyebrow">Note</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500">
                  {rows.length === 0 ? "Nessun prezzo di acquisto configurato." : "Nessun risultato per questa ricerca."}
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} data-testid={`pp-row-${r.id}`} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">{r.product_sku}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Package size={14} className="text-neutral-400 shrink-0" />
                      <span>{r.product_name}</span>
                      {r.product_is_serialized === false && <span className="text-[10px] bg-neutral-200 text-neutral-700 px-1.5 py-0.5 uppercase tracking-wider">Bulk</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Truck size={14} className="text-neutral-400 shrink-0" />
                      {r.supplier_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    € {Number(r.price || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">{r.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-pp-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-pp-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-y-auto" data-testid="pp-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
                <CurrencyEur size={22} className="text-[#002FA7]" /> Prezzo di acquisto
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <span className="label-eyebrow block mb-2">Prodotto *</span>
                <SearchableSelect
                  testid="pp-product"
                  value={form.product_id}
                  options={productOpts}
                  onChange={(v) => setForm({ ...form, product_id: v })}
                  placeholder="Cerca prodotto..."
                />
              </div>
              <div>
                <span className="label-eyebrow block mb-2">Fornitore *</span>
                <SearchableSelect
                  testid="pp-supplier"
                  value={form.supplier_id}
                  options={supplierOpts}
                  onChange={(v) => setForm({ ...form, supplier_id: v })}
                  placeholder="Cerca fornitore..."
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="col-span-2 block">
                  <span className="label-eyebrow block mb-2">Prezzo *</span>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="pp-price"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
                  />
                </label>
                <label className="block">
                  <span className="label-eyebrow block mb-2">Valuta</span>
                  <select
                    data-testid="pp-currency"
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="label-eyebrow block mb-2">Note</span>
                <textarea
                  data-testid="pp-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Es. valido dal 01/01, incluso trasporto..."
                  className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </label>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="pp-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
